import styled from "styled-components";
import { useState, useContext, useEffect } from "react";
import { HotTopicsContext } from "../../../Contexts/HotTopicsContext";
import { DialogWindowContext } from "../../../Contexts/DialogueWindowContext";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { writeToHotTopicsDatabase, deleteHotTopic } from "../../../Firebase";

const HotTopicEditWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px 0;
  margin: 0 auto;
  min-width: 300px;
  align-items: flex-start;
  p {
    font-size: 12px;
    margin-top: 5px;
    text-transform: capitalize;
  }
  .p-inputtext {
    margin: 2px 0 5px 0;
    text-transform: capitalize;
  }
  .p-inputtext,
  .p-treeselect,
  .p-inputwrapper {
    width: 270px;
  }
  .p-inputswitch {
    margin: 5px 0;
  }
`;

const KeyWordsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  width: 270px;
  border: 1px solid #dceaf8;
  border-radius: 6px;
  padding: 5px 3px;
  min-height: 45px;
`;

const Tag = styled.div`
  display: flex;
  justify-items: center;
  align-items: center;
  padding: 3px 7px;
  background-color: #b7b7b7;
  border-radius: 10px;
  color: white;
  width: fit-content;
  height: fit-content;

  h4 {
    font-size: 12px;
    text-transform: capitalize;
  }
  i {
    font-size: 12px;
    margin-left: 5px;
    cursor: pointer;
  }
`;

const NewKeywordWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ButtonsWrapper = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: space-around;
  width: 100%;
`;

export default function HotTopicEdit(props: Props) {
  const { label } = props;
  const hotTopicsContext = useContext(HotTopicsContext);
  const dialogWindowContext = useContext(DialogWindowContext);
  const [hotTopic, setHotTopic] = useState<HotTopic>({
    label: "",
    status: "classified",
    keyWords: [],
    occurrence: 0,
  });
  const [newKeyWord, setNewKeyWord] = useState<string>("");

  const publicHotTopics = hotTopicsContext?.filter(
    (hotTopic) => hotTopic.status == "classified"
  );

  const notPublicHotTopics = hotTopicsContext?.filter(
    (hotTopic) => hotTopic.status == "unclassified"
  );

  const ignoredHotTopics = hotTopicsContext?.filter(
    (hotTopic) => hotTopic.status == "ignored"
  );

  useEffect(() => {
    if (hotTopicsContext) {
      const hotTopic = hotTopicsContext.find(
        (hotTopic) => hotTopic.label === label
      );
      hotTopic && setHotTopic(hotTopic);
    }
  }, [hotTopicsContext]);

  const changeLabel = (e: string) => {
    if (hotTopic) setHotTopic({ ...hotTopic, label: e });
  };

  function deleteKeyword(keyWordToDelete: string) {
    if (hotTopic) {
      const filteredKeyWords = hotTopic.keyWords?.filter(
        (keyWord) => keyWord.trim().toLowerCase() !== keyWordToDelete.trim().toLowerCase() 
      );
      setHotTopic((prevHotTopic) => {
        return { ...prevHotTopic, keyWords: filteredKeyWords };
      });
    }
  }

  function addNewKeyword() {
    if (newKeyWord && hotTopicsContext) {
      //check if Main Label is already in any of the hotTopics label and includes
      const alreadyInUse: string[] = [];
      publicHotTopics.forEach((hotTopicObject) => {
        if (
          hotTopicObject.label.toLowerCase() === newKeyWord.toLowerCase() &&
          hotTopicObject.hotTopicId !== hotTopic?.hotTopicId
        ) {
          alreadyInUse.push(hotTopicObject.label);
        }
        hotTopicObject.keyWords?.forEach((keyWord) => {
          if (
            keyWord.toLowerCase() === newKeyWord.toLowerCase() &&
            hotTopicObject.hotTopicId !== hotTopic?.hotTopicId
          ) {
            alreadyInUse.push(hotTopicObject.label);
          }
        });
      });
      if (alreadyInUse.length > 0) {
        alert("This keyword is already in use in: " + alreadyInUse[0]);
        return;
      }
      const keyWords = hotTopic.keyWords? [...hotTopic.keyWords, newKeyWord]: [newKeyWord];
      setHotTopic({ ...hotTopic, keyWords: keyWords });
      setNewKeyWord("");
    }
  }

  function makeHotTopicNotPublic() {
    if (hotTopic) {
      hotTopic.keyWords?.forEach((keyWord) => {
        const newHotTopic: HotTopic = {
          label: keyWord,
          status: "unclassified",
          keyWords: [],
          occurrence: 0,
        };
        writeToHotTopicsDatabase(newHotTopic);
      });
      const newHotTopic = { ...hotTopic, status: "unclassified" as "unclassified" };
      writeToHotTopicsDatabase(newHotTopic);
      dialogWindowContext.setDialogProps((prevProps) => ({
        ...prevProps,
        showEditWindow: false,
      }));
    }
  }

  function saveHotTopic() {
    if (hotTopic && hotTopicsContext) {
      //check if Main Label is already in any of the hotTopics label and key-words
      const alreadyInUse: string[] = [];
      publicHotTopics.forEach((hotTopicObject) => {
        if (
          hotTopicObject.label.trim().toLowerCase() ===
            hotTopic.label.trim().toLowerCase() &&
          hotTopicObject.hotTopicId !== hotTopic.hotTopicId
        ) {
          alreadyInUse.push(hotTopicObject.label);
        }
        hotTopicObject.keyWords?.forEach((keyWord) => {
          if (
            keyWord.trim().toLowerCase() ===
              hotTopic.label.trim().toLowerCase() &&
            hotTopicObject.hotTopicId !== hotTopic.hotTopicId
          ) {
            alreadyInUse.push(keyWord);
          }
        });
      });
      if (alreadyInUse.length > 0) {
        alert("This MainLabel is already in use in: " + alreadyInUse[0]);
        return;
      } else {
        //check if there is a non public hot topic with the same label or includes
        let nonPublicDuplicate = notPublicHotTopics.find(
          (hotTopicObject) =>
            hotTopicObject.label.trim().toLowerCase() ===
              hotTopic.label.trim().toLowerCase() ||
            hotTopicObject.keyWords?.some(
              (keyword) =>
                keyword.trim().toLowerCase() ===
                hotTopic.label.trim().toLowerCase()
            )
        );

        if (nonPublicDuplicate) {
          //delete nonPublicDuplicate
          writeToHotTopicsDatabase(nonPublicDuplicate);
        }

        //write deleted hot hot topics to non public
        const hotTopicFromContext = hotTopicsContext.find(
          (hotTopicObject) => hotTopicObject.hotTopicId === hotTopic.hotTopicId
        );
        const deletedHotTopics = hotTopicFromContext?.keyWords?.filter(
          (keyWord) =>
            !hotTopic.keyWords?.some(
              (keywordFromArray) =>
                keywordFromArray.trim().toLowerCase() ===
                keyWord.trim().toLowerCase()
            )
        );
        deletedHotTopics &&
          deletedHotTopics.forEach((deletedHotTopic) => {
            const newHotTopic: HotTopic = {
              label: deletedHotTopic,
              status: "unclassified",
              keyWords: [deletedHotTopic],
              occurrence: 0,
            };
            writeToHotTopicsDatabase(newHotTopic);
          });
        //save new keyword
        writeToHotTopicsDatabase(hotTopic);
        dialogWindowContext.setDialogProps((prevProps) => ({
          ...prevProps,
          showEditWindow: false,
        }));
      }
    }
  }

  return (
    <HotTopicEditWrapper>
      <p>Main Label</p>
      <InputText
        id={`label`}
        value={hotTopic?.label}
        onChange={(e) => changeLabel(e.target.value)}
      />
      <p>Associated key words</p>
      <KeyWordsWrapper>
        {hotTopic?.keyWords?.map((keyWord, index) => (
          <Tag key={index}>
            <h4>{keyWord}</h4>
            <i
              className="pi pi-times-circle"
              onClick={() => {
                deleteKeyword(keyWord);
              }}
            ></i>
          </Tag>
        ))}
      </KeyWordsWrapper>
      <p>New Key Words</p>
      <NewKeywordWrapper>
        <InputText
          id={`new-key-word`}
          value={newKeyWord}
          onChange={(e) => {
            setNewKeyWord(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              addNewKeyword();
            }
          }}
        />
        <i className="pi pi-plus" onClick={addNewKeyword}></i>
      </NewKeywordWrapper>
      <ButtonsWrapper>
        {hotTopic.hotTopicId && (
          <Button
            label="Delete"
            style={{ marginTop: "10px", border: "none" }}
            severity="danger"
            icon="pi pi-times"
            onClick={makeHotTopicNotPublic}
          />
        )}

        <Button
          label="Save"
          style={{ marginTop: "10px", border: "none" }}
          severity="success"
          icon="pi pi-check"
          onClick={saveHotTopic}
        />
      </ButtonsWrapper>
    </HotTopicEditWrapper>
  );
}

interface Props {
  label: string;
}
